// Chapter-wise and practice exams mix two difficulty levels 50/50, chosen by exam type.
// level_id: 1 = Easy, 2 = Moderate, 3 = Difficult (same mapping as the admin app).
// jeeOnlyTypes: whether this exam type includes the question types reserved for JEE
// marks: added per correct answer, added (negative = deducted) per wrong answer; unanswered scores 0
export const DIFFICULTY_PATTERNS = {
  KCET: { levels: [1, 2], jeeOnlyTypes: false, marks: { correct: 1, wrong: 0 } },
  JEE: { levels: [2, 3], jeeOnlyTypes: true, marks: { correct: 4, wrong: -1 } },
  NEET: { levels: [2, 3], jeeOnlyTypes: false, marks: { correct: 4, wrong: -1 } },
};

/** KCET / JEE / NEET for an exam: the type the student picked, else the exam's or template's own type. */
export const examPatternOf = (examSettings) => {
  const type = String(examSettings?.difficultyPattern || examSettings?.examType || '').toUpperCase();
  return DIFFICULTY_PATTERNS[type] ? type : null;
};

// Exams with no recognisable type keep the +4 / -1 scheme used before exam types existed
export const marksFor = (examSettings) =>
  DIFFICULTY_PATTERNS[examPatternOf(examSettings)]?.marks ?? { correct: 4, wrong: -1 };

// Numerical (5) and Fill in the Blanks (6), same qtype_ids as the admin app
const JEE_ONLY_TYPES = ['5', '6'];

const questionLevel = (q) => Number(q?.level_id ?? q?.question?.level_id ?? q?.difficulty);
export const questionTopic = (q) => Number(q?.topic_id ?? q?.question?.topic_id);

// ponytail: sort-based shuffle, same as the rest of CreateExam; Fisher–Yates if bias ever matters
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

/** The same chapter can be found under several class/subject key spellings; keep one copy per question. */
export const dedupeQuestions = (pool) =>
  [...new Map(pool.map(q => [q.id ?? q.qcode ?? JSON.stringify(q), q])).values()];

/**
 * Pick n questions: half from each allowed level (odd extra goes to the first, easier level).
 * If one level runs short, the other level fills the gap. Questions of any other level are never used.
 */
export const pickByDifficulty = (pool, n, [first, second], take = takeRandom) => {
  const a = pool.filter(q => questionLevel(q) === first);
  const b = pool.filter(q => questionLevel(q) === second);
  const takeB = Math.min(b.length, n - Math.min(a.length, Math.ceil(n / 2)));
  const takeA = Math.min(a.length, n - takeB);
  return shuffle([...take(a, takeA), ...take(b, takeB)]);
};

const questionType = (q) => String(q?.qtype_id ?? q?.question?.qtype_id ?? 'unknown');

/** Every exam-type question pick goes through here: drops types the exam doesn't use, then mixes difficulty. */
export const pickForExamType = (pool, n, examType, take) => {
  const { levels, jeeOnlyTypes } = DIFFICULTY_PATTERNS[examType];
  const allowed = jeeOnlyTypes ? pool : pool.filter(q => !JEE_ONLY_TYPES.includes(questionType(q)));
  return pickByDifficulty(allowed, n, levels, take);
};

const takeRandom = (qs, k) => shuffle(qs).slice(0, k);

/**
 * Take k questions round-robin across question types (MCQ, Match, Assertion, …), so every type
 * present gets an equal share (±1). A type that runs out is skipped and the others fill its place.
 */
export const spreadByType = (qs, k) => {
  const byType = {};
  qs.forEach(q => (byType[questionType(q)] ||= []).push(q));
  const groups = shuffle(Object.values(byType)).map(shuffle);
  const out = [];
  for (let i = 0; out.length < k && groups.some(g => i < g.length); i++) {
    for (const g of groups) if (i < g.length && out.length < k) out.push(g[i]);
  }
  return out;
};

export const getDifficultyLevelUI = (levelId) => {
  switch (Number(levelId)) {
    case 1: return { label: "Easy", color: "#16a34a" };
    case 2: return { label: "Moderate", color: "#ea580c" };
    case 3: return { label: "Difficult", color: "#dc2626" };
    default: return { label: "Unknown", color: "#64748b" };
  }
};
